// Ordered registry of the documentation site: for each page, its route, its
// in-page sections, and — as i18n keys — its name and one-line summary. The
// single source of truth for those, so a page's name lives in exactly one
// place (its `docs.*.label` catalog entry).
//
// Each section `id` must match the `id` on the corresponding <h2> in the page
// component so the sidebar links and scroll-spy line up.

interface DocsSection {
  id: string;
  label: string;
}

export interface DocsPage {
  path: string;
  // i18n keys (`docs.*` namespace), resolved via useT: `labelKey` is the
  // page's name, `descKey` its one-line summary.
  labelKey: string;
  descKey: string;
  sections: DocsSection[];
}

export const docsPages: DocsPage[] = [
  {
    path: '/docs',
    labelKey: 'docs.getting-started.label',
    descKey: 'docs.getting-started.desc',
    sections: [
      { id: 'what-is-goalpost', label: 'What Goalpost does' },
      { id: 'create-account', label: 'Create an account' },
      { id: 'first-document', label: 'Create your first document' },
      { id: 'add-details', label: 'Add content' },
      { id: 'submit-change', label: 'Submit a change' },
      { id: 'invite-team', label: 'Bring in your team' },
      { id: 'next-steps', label: 'Next steps' },
    ],
  },
  {
    path: '/docs/workspace',
    labelKey: 'docs.workspace.label',
    descKey: 'docs.workspace.desc',
    sections: [
      { id: 'intro', label: 'Your workspace' },
      { id: 'access', label: 'Administrators and members' },
      { id: 'teams', label: 'Teams' },
      { id: 'documents', label: 'Documents' },
      { id: 'approval-rules', label: 'Approval rules' },
      { id: 'account-types', label: 'Account types' },
      { id: 'invitations', label: 'Invitations' },
      { id: 'billing', label: 'Billing' },
      { id: 'notifications', label: 'Notifications' },
    ],
  },
  {
    path: '/docs/documents',
    labelKey: 'docs.documents.label',
    descKey: 'docs.documents.desc',
    sections: [
      { id: 'intro', label: 'About documents' },
      { id: 'create', label: 'Create a document' },
      { id: 'delete', label: 'Delete a document' },
      { id: 'permissions', label: 'Document permissions' },
      { id: 'approvals', label: 'Approvals' },
      { id: 'membership', label: 'Document membership' },
    ],
  },
  {
    path: '/docs/change-management',
    labelKey: 'docs.change-management.label',
    descKey: 'docs.change-management.desc',
    sections: [
      { id: 'intro', label: 'Change batches' },
      { id: 'lifecycle', label: 'The approval lifecycle' },
      { id: 'history', label: 'View history' },
      { id: 'draft-checkpoints', label: 'Review and undo draft checkpoints' },
      { id: 'copy', label: 'Copy a draft' },
      { id: 'merge', label: 'Merge drafts' },
      { id: 'split', label: 'Split into a new draft' },
    ],
  },
  {
    path: '/docs/approval-rules',
    labelKey: 'docs.approval-rules.label',
    descKey: 'docs.approval-rules.desc',
    sections: [
      { id: 'intro', label: 'About approval rules' },
      { id: 'create', label: 'Create an approval rule' },
      { id: 'apply-document', label: 'Apply a rule to a document' },
      { id: 'apply-detail', label: 'Apply a rule to a detail' },
      { id: 'edit', label: 'Edit an approval rule' },
    ],
  },
  {
    path: '/docs/details',
    labelKey: 'docs.details.label',
    descKey: 'docs.details.desc',
    sections: [
      { id: 'intro', label: 'How editing works' },
      { id: 'edit', label: 'Edit contents' },
      { id: 'move', label: 'Move contents' },
      { id: 'delete', label: 'Delete contents' },
      { id: 'approval', label: 'Set an approval rule' },
      { id: 'labels', label: 'Labels' },
      { id: 'structure', label: 'Structuring a document' },
    ],
  },
  {
    path: '/docs/teams',
    labelKey: 'docs.teams.label',
    descKey: 'docs.teams.desc',
    sections: [
      { id: 'intro', label: 'About teams' },
      { id: 'create', label: 'Create a team' },
      { id: 'add-to-document', label: 'Add a team to a document' },
      { id: 'remove-from-document', label: 'Remove a team from a document' },
      { id: 'archive', label: 'Archive a team' },
      { id: 'delete', label: 'Delete a team' },
      { id: 'rules', label: 'Team approval rules' },
      { id: 'invite', label: 'Invite someone to a team' },
      { id: 'remove-member', label: 'Remove someone from a team' },
    ],
  },
  {
    path: '/docs/ai-integration',
    labelKey: 'docs.ai-integration.label',
    descKey: 'docs.ai-integration.desc',
    sections: [
      { id: 'overview', label: 'How it works' },
      { id: 'server-url', label: 'Your connection address' },
      { id: 'connect', label: 'Connect your assistant' },
      { id: 'what-can', label: 'What it can do' },
    ],
  },
];

export function docsPageIndex(path: string): number {
  return docsPages.findIndex((page) => page.path === path);
}
